import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { PessoaDTO, PessoaFormDTO } from '../../../../models/clientes/pessoa.model';
import { TipoPessoa } from '../../../../enums/tipoPessoa.enum';
import { TipoNaturezaFiscal } from '../../../../enums/tipoNaturezaFiscal.enum';
import { Sexo } from '../../../../enums/sexo.enum';
import { CidadeService } from '../../../../services/configuracoes/cidade.service';
import { PessoaService } from '../../../../services/pessoas/pessoas.service';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { PaisDDI } from '../../../../models/cardapio-digital/cardapio-digital.model';
import { PAISES_DDI } from '../../../../shared/data/paises-ddi.data';

@Component({
  selector: 'app-fornecedor-edit',
  templateUrl: './fornecedor-edit.component.html',
  styleUrls: ['./fornecedor-edit.component.scss'],
  providers: [MessageService],
})
export class FornecedorEditComponent implements OnInit, OnDestroy {
  form: PessoaFormDTO = this.createEmptyForm();
  submitting = false;
  cidadeList: { label: string; value: string }[] = [];
  tipoPessoa: { label: string; value: TipoPessoa }[] = [];
  tipoNaturezaFiscal: { label: string; value: TipoNaturezaFiscal }[] = [];
  sexo: { label: string; value: Sexo }[] = [];

  // DDI dropdown
  paisSelecionado: PaisDDI = PAISES_DDI[0];

  // Objetos de traducao
  texts = {
    titulo: '',
    nome: '',
    nome_fantasia: '',
    ruc: '',
    ci: '',
    email: '',
    telefone: '',
    endereco: '',
    numero: '',
    cidade: '',
    tipo_pessoa: '',
    tipo_natureza_fiscal: '',
    data_nascimento: '',
    sexo: ''
  };

  placeholders = {
    nome: '',
    nome_fantasia: '',
    ruc: '',
    ci: '',
    email: '',
    telefone: '',
    endereco: '',
    numero: '',
    selecione_cidade: '',
    selecione_tipo_pessoa: '',
    selecione_natureza_fiscal: '',
    data_nascimento: '',
    selecione_sexo: ''
  };

  botoes = {
    enviar: ''
  };

  private langChangeSub!: Subscription;

  constructor(
    private messageService: MessageService,
    private pessoaService: PessoaService,
    private cidadeService: CidadeService,
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.loadTranslations();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });

    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.loadCliente(Number(id));
    }

    this.loadDropdowns();
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  private loadTranslations(): void {
    this.texts = {
      titulo: this.translate.instant('fornecedor-edit.texts.titulo'),
      nome: this.translate.instant('fornecedor-edit.labels.nome'),
      nome_fantasia: this.translate.instant('fornecedor-edit.labels.nome_fantasia'),
      ruc: this.translate.instant('fornecedor-edit.labels.ruc'),
      ci: this.translate.instant('fornecedor-edit.labels.ci'),
      email: this.translate.instant('fornecedor-edit.labels.email'),
      telefone: this.translate.instant('fornecedor-edit.labels.telefone'),
      endereco: this.translate.instant('fornecedor-edit.labels.endereco'),
      numero: this.translate.instant('fornecedor-edit.labels.numero'),
      cidade: this.translate.instant('fornecedor-edit.labels.cidade'),
      tipo_pessoa: this.translate.instant('fornecedor-edit.labels.tipo_pessoa'),
      tipo_natureza_fiscal: this.translate.instant('fornecedor-edit.labels.tipo_natureza_fiscal'),
      data_nascimento: this.translate.instant('fornecedor-edit.labels.data_nascimento'),
      sexo: this.translate.instant('fornecedor-edit.labels.sexo')
    };

    this.placeholders = {
      nome: this.translate.instant('fornecedor-edit.placeholders.nome'),
      nome_fantasia: this.translate.instant('fornecedor-edit.placeholders.nome_fantasia'),
      ruc: this.translate.instant('fornecedor-edit.placeholders.ruc'),
      ci: this.translate.instant('fornecedor-edit.placeholders.ci'),
      email: this.translate.instant('fornecedor-edit.placeholders.email'),
      telefone: this.translate.instant('fornecedor-edit.placeholders.telefone'),
      endereco: this.translate.instant('fornecedor-edit.placeholders.endereco'),
      numero: this.translate.instant('fornecedor-edit.placeholders.numero'),
      selecione_cidade: this.translate.instant('fornecedor-edit.placeholders.selecione_cidade'),
      selecione_tipo_pessoa: this.translate.instant('fornecedor-edit.placeholders.selecione_tipo_pessoa'),
      selecione_natureza_fiscal: this.translate.instant('fornecedor-edit.placeholders.selecione_natureza_fiscal'),
      data_nascimento: this.translate.instant('fornecedor-edit.placeholders.data_nascimento'),
      selecione_sexo: this.translate.instant('fornecedor-edit.placeholders.selecione_sexo')
    };

    this.botoes = {
      enviar: this.translate.instant('fornecedor-edit.botoes.enviar')
    };
  }

  loadCliente(id: number): void {
    this.pessoaService.getById('/pessoas', String(id)).subscribe((response: PessoaDTO) => {
      this.form = {
        id: response.id,
        nome: response.nome,
        fantasia: response.fantasia,
        ruc: response.ruc,
        ci: response.ci,
        email: response.email,
        telefone: response.telefone,
        endereco: response.endereco,
        numero: response.numero,
        categoriaCliente: Boolean(response.categoriaCliente),
        categoriaFornecedor: Boolean(response.categoriaFornecedor),
        categoriaVendedor: Boolean(response.categoriaVendedor),
        cidade: {
          label: String(response.cidadeNome || ''), // Converte para string diretamente
          value: Number(response.cidade_id), // Converte para número diretamente
        },
        tipoPessoa: {
          label: String(response.tipoPessoa),
          value: response.tipoPessoa,
        },
        tipoNaturezaFiscal: {
          label: String(response.tipoNaturezaFiscal),
          value: response.tipoNaturezaFiscal,
        },
        dataNascimento: response.dataNascimento
        ? new Date(response.dataNascimento.split('/').reverse().join('-')).toISOString().split('T')[0]
        : '', // Converte DD/MM/YYYY para Date e depois para string
        sexo: {
          label: response.sexo ? String(response.sexo) : 'Masculino',
          value: response.sexo ?? Sexo.MASCULINO,
        },
      };

      // Converter DDI string para PaisDDI
      const ddiComPlus = '+' + (response.ddi || '595');
      this.paisSelecionado = PAISES_DDI.find(p => p.ddi === ddiComPlus) || PAISES_DDI[0];
    });
  }




  loadDropdowns(): void {
    // Carregar as cidades
    this.cidadeService.get('/cidade?listarTodos=true').subscribe((response) => {
      this.cidadeList = response.content.map((cidade: any) => ({
        label: cidade.nome,
        value: String(cidade.id),
      }));
    });

    // Carregar os valores dos enums
    this.tipoPessoa = this.getDropItems(TipoPessoa);
    this.tipoNaturezaFiscal = this.getDropItems(TipoNaturezaFiscal);
    this.sexo = this.getDropItems(Sexo);
  }

  getDropItems<T extends object>(enumType: T): { label: string; value: T[keyof T] }[] {
    return Object.values(enumType).map((item) => ({
      label: String(item),
      value: item as T[keyof T],
    }));
  }

  submit(): void {
    if (this.submitting) return;
    if (!this.form.tipoPessoa || !this.form.tipoNaturezaFiscal || !this.form.sexo || !this.form.cidade || !this.form.nome || !this.form.email) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('fornecedor.mensagens.campos_obrigatorios'),
      });
      return;
    }

    const body: PessoaDTO = {
      nome: this.form.nome,
      fantasia: this.form.fantasia != '' ? this.form.fantasia : undefined,
      ruc: this.form.ruc || undefined,
      ci: this.form.ci || undefined,
      email: this.form.email,
      ddi: this.paisSelecionado.ddi.replace('+', '') || '595',
      telefone: this.form.telefone?.replace(/\D/g, '') || undefined,
      endereco: this.form.endereco,
      numero: this.form.numero,
      cidade_id: Number(this.form.cidade.value),
      categoriaCliente: this.form.categoriaCliente,
      categoriaFornecedor: this.form.categoriaFornecedor,
      categoriaVendedor: this.form.categoriaVendedor,
      tipoPessoa: this.form.tipoPessoa.value,
      tipoNaturezaFiscal: this.form.tipoNaturezaFiscal.value,
      dataNascimento: this.form.dataNascimento,
      sexo: this.form.sexo.value,
    };

    this.submitting = true;
    this.pessoaService.put(`/pessoas/${this.form.id}`, body).subscribe({
      next: () => {
        this.submitting = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('fornecedor.mensagens.fornecedor_editado'),
        });
        this.router.navigate(['/lista-de-fornecedores']);
      },
      error: () => {
        this.submitting = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: this.translate.instant('fornecedor.mensagens.falha_editar'),
        });
      },
    });
  }

  onTipoPessoaChange(): void {
    if (this.form.tipoPessoa?.value === TipoPessoa.FISICA) {
      this.form.ruc = '';
    } else if (this.form.tipoPessoa?.value === TipoPessoa.JURIDICA) {
      this.form.ci = '';
    }
  }

  createEmptyForm(): PessoaFormDTO {
    return {
      nome: '',
      fantasia: '',
      ruc: '',
      ci: '',
      email: '',
      telefone: '',
      endereco: '',
      numero: '',
      cidade: undefined,
      categoriaCliente: false,
      categoriaFornecedor: true,
      categoriaVendedor: false,
      tipoPessoa: undefined,
      tipoNaturezaFiscal: undefined,
      dataNascimento: '',
      sexo: undefined,
    };
  }
}
